import { useEffect, useRef, useState } from 'react'

import Cookies from 'js-cookie'
import { FormattedMessage, useIntl } from 'react-intl'

import ActionIcon from '@components/UI/ActionIcon'
import TrackedRoute from '@components/UI/TrackedRoute/TrackedRoute'
import { INBOX_FILTER } from '@constants'
import { ISelectedSubmissionsContextProps } from '@context/SelectedSubmissions'
import { EventTrackingNames } from '@enums'
import { withSelectedSubmissions } from '@hocs'
import { ICampaign } from '@store/campaigns'
import Theme from '@theme'
import { Button, Header, Text } from '@tribegroup'
import { showCookieToast } from '@utils'
import { ActionIconWrapper } from '../TitleBar.styled'
import InboxTitleBarSelectionModeBulkDecline from './Modal/BulkDecline/BulkDecline'
import InboxTitleBarSelectionModeBulkFeedback from './Modal/BulkFeedback/BulkFeedback'
import {
  CancelButton,
  CancelButtonWrapper,
  InboxTitleBarSelectionModeStyled,
  SelectAll,
  SelectedCount,
  SelectionModeActions,
} from './SelectionMode.styled'

export interface IInboxTitleBarSelectionModeProps {
  campaign: ICampaign
  filter: string
  sortBy?: string
  onSelectAllSubmissions: VoidFunction
  onCancel: VoidFunction
  hasBulkFeedbackPermission: boolean
  hasBulkDeclinePermission: boolean
  totalSubmissions: number
}

interface IComponentProps
  extends IInboxTitleBarSelectionModeProps,
    Pick<ISelectedSubmissionsContextProps, 'selectedSubmissionIds' | 'hideToast'> {}

export const InboxTitleBarSelectionMode = ({
  campaign,
  filter = INBOX_FILTER,
  onSelectAllSubmissions,
  onCancel,
  hasBulkFeedbackPermission,
  hasBulkDeclinePermission,
  totalSubmissions,
  selectedSubmissionIds,
  hideToast,
}: IComponentProps) => {
  const intl = useIntl()

  const selectedCount = selectedSubmissionIds.length

  const [isOpenModalBulkDecline, setIsOpenModalBulkDecline] = useState(false)
  const [isOpenModalBulkFeedback, setIsOpenModalBulkFeedback] = useState(false)

  const wasModalOpenRef = useRef(false)

  useEffect(() => {
    const wasOpen = wasModalOpenRef.current
    const isOpen = isOpenModalBulkDecline || isOpenModalBulkFeedback
    wasModalOpenRef.current = isOpen
    if (wasOpen && !isOpen && !Cookies.get('tribecookieread')) {
      showCookieToast()
    }
  }, [isOpenModalBulkDecline, isOpenModalBulkFeedback])

  const onBulkDeclineClick = () => {
    if (selectedCount > 0) {
      hideToast()
      setIsOpenModalBulkDecline(true)
    }
  }

  const onBulkFeedbackClick = () => {
    if (selectedCount > 0) {
      hideToast()
      setIsOpenModalBulkFeedback(true)
    }
  }

  const onModalCancel = () => {
    setIsOpenModalBulkFeedback(false)
    setIsOpenModalBulkDecline(false)
  }

  const hasSelectedSubmissions = selectedCount > 0
  const isBulkDeclineOperationAllowed =
    hasBulkDeclinePermission && (filter === INBOX_FILTER || filter === 'shortlisted')

  return (
    <InboxTitleBarSelectionModeStyled full alignCenter justifyBetween>
      <InboxTitleBarSelectionModeBulkDecline
        submissionIds={selectedSubmissionIds}
        campaignId={campaign.id}
        isOpen={isOpenModalBulkDecline}
        onCancel={onModalCancel}
      />
      <InboxTitleBarSelectionModeBulkFeedback
        submissionIds={selectedSubmissionIds}
        campaignId={campaign.id}
        isOpen={isOpenModalBulkFeedback}
        onCancel={onModalCancel}
      />
      <SelectionModeActions alignCenter justifyStart>
        <TrackedRoute
          eventName={EventTrackingNames.BulkSelectSelectAll}
          properties={{
            brief_id: campaign.id,
            role: campaign.role,
            current_filter: filter,
          }}
        >
          <SelectAll onClick={onSelectAllSubmissions}>
            <Button outlined small noFocus>
              <Text>
                <FormattedMessage id="inbox.cardlist.selection.selectAll" />
              </Text>
            </Button>
          </SelectAll>
        </TrackedRoute>
        {hasBulkFeedbackPermission && (
          <ActionIconWrapper>
            <ActionIcon
              glyph="feedback"
              color={hasSelectedSubmissions ? Theme.grey900Color : Theme.grey700Color}
              tooltip={intl.formatMessage({ id: 'submission.card.action.feedback' })}
              size={1.5}
              disabled={!hasSelectedSubmissions}
              onClick={onBulkFeedbackClick}
            />
          </ActionIconWrapper>
        )}
        {isBulkDeclineOperationAllowed && (
          <ActionIconWrapper>
            <ActionIcon
              glyph="decline-submission"
              color={hasSelectedSubmissions ? Theme.grey900Color : Theme.grey700Color}
              tooltip={intl.formatMessage({ id: 'submission.card.action.decline' })}
              size={1.5}
              disabled={!hasSelectedSubmissions}
              onClick={onBulkDeclineClick}
            />
          </ActionIconWrapper>
        )}
      </SelectionModeActions>
      <SelectedCount hilightCount={hasSelectedSubmissions}>
        <Header color={Theme.grey700Color}>
          <FormattedMessage
            id="inbox.cardlist.selection.count"
            values={{
              count: selectedCount,
              total: totalSubmissions,
              span: (chunks) => <span className="count">{chunks}</span>,
            }}
          />
        </Header>
      </SelectedCount>
      <CancelButtonWrapper>
        <CancelButton onClick={onCancel}>
          <Text>
            <FormattedMessage id="core.text.cancel" />
          </Text>
        </CancelButton>
      </CancelButtonWrapper>
    </InboxTitleBarSelectionModeStyled>
  )
}

export default withSelectedSubmissions(InboxTitleBarSelectionMode)
